import * as React from "react"
import { Modal, Button, Container, Col, Row } from "react-bootstrap"
import { FaAngleDown } from "react-icons/fa"
import { Typeahead } from "react-bootstrap-typeahead"
import 'react-bootstrap-typeahead/css/Typeahead.css'
import { Subscription } from "rxjs"
import { AutoFilterLocationWithoutRegionGetObservable } from "../../api/Location/AutoFilterLocationsGet"
import AutoFilterLocation from "../../api/Location/LocationModel/AutoFilterLocation"
import LabServiceRegulatoryModesGetObservable from "../../api/LabService/LabServiceRegulatoryModesGet"
import RegulatoryMode from "../../api/LabService/LabServiceModel/RegulatoryMode"

interface PoolMembersFiltersModalProps {
  show: boolean
  selectedLocations: AutoFilterLocation[]
  selectedRegulatoryModes: RegulatoryMode[]
  onSave: (locations: AutoFilterLocation[], regulatoryModes: RegulatoryMode[], close: boolean) => void
  onClose: () => void
}

interface PoolMembersFiltersModalState {
  availableLocations?: AutoFilterLocation[]
  isLocationsLoading: boolean
  selectedLocations: AutoFilterLocation[]
  availableRegulatoryModes?: RegulatoryMode[]
  isRegulatoryModesLoading: boolean
  selectedRegulatoryModes: RegulatoryMode[]
}

export default class PoolMembersFiltersModal extends React.Component<PoolMembersFiltersModalProps, PoolMembersFiltersModalState> {
  state: PoolMembersFiltersModalState = {
    isLocationsLoading: false,
    selectedLocations: this.props.selectedLocations,
    isRegulatoryModesLoading: false,
    selectedRegulatoryModes: this.props.selectedRegulatoryModes
  }

  subscriptions = new Subscription()

  locationTypeahead = React.createRef<Typeahead<AutoFilterLocation>>()
  regulatoryModeTypeahead = React.createRef<Typeahead<RegulatoryMode>>()

  componentDidMount() {
    this.setState({ isLocationsLoading: true, isRegulatoryModesLoading: true })
    this.subscriptions.add(
      AutoFilterLocationWithoutRegionGetObservable().subscribe(
        (data: AutoFilterLocation[]) => this.setState({ availableLocations: data, isLocationsLoading: false }),
        () => this.setState({ isLocationsLoading: false })
      )
    )

    this.subscriptions.add(
      LabServiceRegulatoryModesGetObservable().subscribe(
        (data: RegulatoryMode[]) => this.setState({ availableRegulatoryModes: data, isRegulatoryModesLoading: false }),
        () => this.setState({ isRegulatoryModesLoading: false })
      )
    )
  }

  componentWillUnmount() {
    this.subscriptions.unsubscribe()
  }

  componentDidUpdate(prevProps: PoolMembersFiltersModalProps, prevState: PoolMembersFiltersModalState) {
    if (!(this.isLocationsEqual(this.props.selectedLocations, prevProps.selectedLocations)
      && this.isRegulatoryModesEqual(this.props.selectedRegulatoryModes, prevProps.selectedRegulatoryModes))) {
      this.setState({
        selectedLocations: this.props.selectedLocations,
        selectedRegulatoryModes: this.props.selectedRegulatoryModes
      })
    }
  }

  isLocationsEqual = (items1: AutoFilterLocation[], items2: AutoFilterLocation[]) => {
    const itemsString = (items: AutoFilterLocation[]) => items.map(item => item.Name).sort().join(",")
    return itemsString(items1) === itemsString(items2)
  }

  isRegulatoryModesEqual = (items1: RegulatoryMode[], items2: RegulatoryMode[]) => {
    const itemsString = (items: RegulatoryMode[]) => items.map(item => item.Name).sort().join(",")
    return itemsString(items1) === itemsString(items2)
  }

  handleSave = () => this.props.onSave(this.state.selectedLocations, this.state.selectedRegulatoryModes, true)

  handleClearAll = () => {
    this.setState({ selectedLocations: [], selectedRegulatoryModes: [] })

    const { selectedLocations, selectedRegulatoryModes } = this.props
    const filterAlreadySet = selectedLocations.length > 0 || selectedRegulatoryModes.length > 0
    if (filterAlreadySet) {
      this.props.onSave([], [], false)
    }
  }

  handleClose = () => {
    this.setState({ selectedLocations: this.props.selectedLocations, selectedRegulatoryModes: this.props.selectedRegulatoryModes })
    this.props.onClose()
  }

  handleAddLocation = (items: AutoFilterLocation[]) => {
    if (items[0] && !this.state.selectedLocations.find(item => item.Name === items[0].Name)) {
      this.setState({ selectedLocations: [...this.state.selectedLocations, items[0]] })
    }
  }

  handleRemoveLocation = (removeItem: AutoFilterLocation) => {
    this.setState({ selectedLocations: this.state.selectedLocations.filter(item => item.Name !== removeItem.Name) })
  }

  handleAddRegulatoryMode = (items: RegulatoryMode[]) => {
    if (items[0] && !this.state.selectedRegulatoryModes.find(item => item.RegulatoryModeID === items[0].RegulatoryModeID)) {
      this.setState({ selectedRegulatoryModes: [...this.state.selectedRegulatoryModes, items[0]] })
    }
  }

  handleRemoveRegulatoryMode = (removeItem: RegulatoryMode) => {
    this.setState({ selectedRegulatoryModes: this.state.selectedRegulatoryModes.filter(item => item.RegulatoryModeID !== removeItem.RegulatoryModeID) })
  }

  render() {
    const { selectedLocations, selectedRegulatoryModes } = this.state

    const hasChanges = !(this.isLocationsEqual(this.props.selectedLocations, selectedLocations)
      && this.isRegulatoryModesEqual(this.props.selectedRegulatoryModes, selectedRegulatoryModes))
    const canClearAll = hasChanges || selectedLocations.length > 0 || selectedRegulatoryModes.length > 0

    return <Modal backdrop="static" size="lg" centered show={this.props.show} onHide={this.handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>ADVANCED FILTERS</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container fluid className="AdvancedFiltersModal">
          <Row>
            <Col md={6}>
              <div className="typeahead">
                <Typeahead id="location"
                  ref={this.locationTypeahead}
                  labelKey="Name"
                  filterBy={(option, props) => props.selected.length > 0 || option.Name.toLowerCase().indexOf(props.text.toLowerCase()) !== -1}
                  onChange={this.handleAddLocation}
                  disabled={this.state.isLocationsLoading}
                  options={(this.state.availableLocations || []).filter(item => !this.state.selectedLocations.includes(item))}
                  selected={[]}
                  emptyLabel="No Locations Found"
                  placeholder="Location">
                  {!this.state.isLocationsLoading &&
                    <Button variant="toggle" onClick={() => { this.locationTypeahead.current?.toggleMenu() }}>
                      <FaAngleDown />
                    </Button>}
                </Typeahead>
              </div>
              <div className="selection">
                {this.state.selectedLocations.map(item => <div key={item.Name}>
                  <span>{item.Name}</span>
                  <span className="remove" onClick={() => this.handleRemoveLocation(item)}>×</span>
                </div>)}
              </div>
            </Col>
            <Col md={6}>
              <div className="typeahead">
                <Typeahead id="regMode"
                  ref={this.regulatoryModeTypeahead}
                  labelKey="Name"
                  filterBy={(option, props) => props.selected.length > 0 || option.Name.toLowerCase().indexOf(props.text.toLowerCase()) !== -1}
                  onChange={this.handleAddRegulatoryMode}
                  disabled={this.state.isRegulatoryModesLoading}
                  options={(this.state.availableRegulatoryModes || []).filter(item => !this.state.selectedRegulatoryModes.includes(item))}
                  selected={[]}
                  emptyLabel="No Regulatory Modes Found"
                  placeholder="Regulatory Mode">
                  {!this.state.isRegulatoryModesLoading && <Button variant="toggle" onClick={() => { this.regulatoryModeTypeahead.current?.toggleMenu() }}>
                    <FaAngleDown />
                  </Button>}
                </Typeahead>
              </div>
              <div className="selection">
                {this.state.selectedRegulatoryModes.map(item => <div key={item.RegulatoryModeID}>
                  <span>{item.Name}</span>
                  <span className="remove" onClick={() => this.handleRemoveRegulatoryMode(item)}>×</span>
                </div>)}
              </div>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
      <Modal.Footer>
        {hasChanges && <Button variant="primary" onClick={() => this.handleSave()}>APPLY</Button>}
        {canClearAll && <Button variant="primary" onClick={() => this.handleClearAll()}>CLEAR ALL</Button>}
        {!hasChanges && <Button variant="secondary" onClick={() => this.handleClose()}>CLOSE</Button>}
      </Modal.Footer>
    </Modal >
  }
}
